<script setup lang="ts">
import I18n from '@/addons/i18n'
import { checkLinkClick } from '@/addons/functions'

const props = defineProps({
  icon: {
    type: String,
    required: false,
    default: null,
  },
  label: {
    type: String,
    required: false,
    default: null,
  },
  sections: {
    type: Array,
    required: false,
    default: null,
  },
  link: {
    type: String,
    required: false,
    default: null,
  },
  qs: {
    type: String,
    required: false,
    default: null,
  },
})

const emits = defineEmits(['click', 'mouseenter', 'mouseleave'])

const onClick = async (e) => {
  if (props.link || props.qs) {
    await checkLinkClick(
      {
        attributes: props,
      },
      'boh', // TODO: what is context here?
    )
  } else {
    emits('click', e)
  }
}

const onMouseEnter = (e) => {
  emits('mouseenter', e)
}

const onMouseLeave = (e) => {
  emits('mouseleave', e)
}
</script>

<template>
  <div class="abs-core__navigation-item">
    <button
      class="abs-core__navigation-item__button"
      color="icon"
      @click="onClick"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <fe-icon v-if="props.icon" :icon="props.icon" class="fe-icon-leading" />
      <span v-if="props.label" class="fe-label">
        {{ I18n.global.t(props.label) }}
      </span>
      <fe-icon
        v-if="props.sections && props.sections.length"
        class="fe-arrow"
        icon="chevron-right"
      />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.abs-core__navigation {
  &.openDrawer,
  &-second_level {
    .abs-core__navigation-item {
      &__button {
        justify-content: start;
        aspect-ratio: unset;
        width: 100%;
        border-radius: 0.5rem;

        .fe-label,
        .fe-arrow {
          opacity: 1;
          max-width: 200px;
          font-weight: 500;
          transition:
            opacity 0.2s ease 0.05s,
            max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
      }

      /* Hover pill only when sidebar is expanded or in second level */
      &:hover &__button {
        background-color: rgba(var(--color-tertiary-light) / 0.35);
      }
    }
  }

  &-item {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    &.submenu &__button {
      height: 36px;
      font-size: 1rem;
      font-weight: 400;

      .fe-label {
        font-weight: 400;
      }
    }

    &.active &__button {
      color: #4689f6;

      .fe-icon-leading :deep(ion-icon),
      .fe-arrow :deep(ion-icon) {
        color: #4689f6;
      }

      .fe-label {
        color: #4689f6;
        font-weight: 600;
      }
    }

    /* Expanded section: rotate arrow downward */
    &.expanded &__button {
      .fe-arrow {
        transform: rotate(90deg);
      }
    }

    &__button {
      display: flex;
      align-items: center;
      justify-content: center;
      aspect-ratio: 1 / 1;
      height: 40px;
      font-size: 1.23rem;
      font-weight: 600;
      font-family: Switzer, sans-serif;
      color: #636363;
      background: transparent;
      padding: 0 1rem;
      border: none;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        color 0.15s ease;

      .fe-icon-leading {
        margin-left: 0 !important;
        margin-right: 0 !important;
        font-size: 2rem;
        display: flex;

        :deep(ion-icon) {
          color: #636363;
        }
      }

      .fe-label {
        color: #636363;
        margin-left: 0;
      }

      .fe-icon-leading + .fe-label {
        margin-left: 1rem;
      }

      .fe-label,
      .fe-arrow {
        display: flex;
        opacity: 0;
        max-width: 0;
        overflow: hidden;
        white-space: nowrap;
        transition:
          opacity 0.15s ease,
          max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .fe-arrow {
        margin-left: 5px;
        font-size: 1rem;
        transition:
          transform 0.15s ease-in-out,
          opacity 0.15s ease,
          max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1);

        :deep(ion-icon) {
          color: #636363;
        }
      }

      /* style the “native” shadow part */
      &::part(native) {
        padding: 0;
        justify-content: flex-start !important;

        ::part(inner) {
          background-color: red !important;
        }
      }
    }
  }
}
</style>
